// src/pages/safety/SafetyBoard.jsx
import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";

import PageHeader from "../../components/common/ui/PageHeader";
import BoardToolbar from "../../components/common/board/BoardToolbar";
import BoardTable from "../../components/common/board/BoardTable";
import BoardPagination from "../../components/common/board/BoardPagination";
import SearchBar from "../../components/common/board/SearchFilterBar";

import { listSafetyDocs } from "../../api/safety";

// ---------------------------------------
// Safety 타입 정의
// ---------------------------------------
const DOC_TYPES = [
  { key: "report", label: "안전관리 계획서" },
  { key: "check", label: "안전 점검" },
  { key: "scar", label: "SCAR" },
  { key: "incident", label: "사고 이력" },
  { key: "riskcheck", label: "위험성 평가서" },
];

const getDocTypeLabel = (key) =>
  DOC_TYPES.find((t) => t.key === key)?.label || "-";

// 상태 Badge 스타일
const STATUS_LABEL = {
  draft: "임시저장",
  approved: "승인",
  rejected: "반려",
  in_progress: "진행 중",
  submit: "상신",
  cancel: "취소",
  none: "-",
};

const STATUS_COLOR = {
  draft: "bg-gray-100 text-gray-700",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  in_progress: "bg-yellow-100 text-yellow-800",
  submit: "bg-yellow-100 text-yellow-800",
  cancel: "bg-gray-100 text-gray-700",
  none: "bg-gray-50 text-gray-500",
};

// ✅ SCAR(및 진행형 문서) 진행상태(meta.progress) 라벨
const PROGRESS_LABEL = {
  planned: "점검 예정",
  action_needed: "조치 필요",
  in_progress: "조치 중",
  urgent: "긴급",
  cancelled: "취소",
  completed: "조치 완료",
  fail: "부적합",
  failed: "부적합",
};

const PROGRESS_COLOR = {
  planned: "bg-blue-100 text-blue-800",
  action_needed: "bg-yellow-100 text-yellow-800",
  in_progress: "bg-yellow-100 text-yellow-800",
  urgent: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-700",
  completed: "bg-green-100 text-green-800",
  fail: "bg-red-100 text-red-800",
  failed: "bg-red-100 text-red-800",
};

// ---------------------------------------
// Main Component
// ---------------------------------------
export default function SafetyBoard() {
  const projectId = localStorage.getItem("projectId");
  const navigate = useNavigate();
  const location = useLocation();
  const { doc_type } = useParams();

  const isSingleTypeMode = doc_type && doc_type !== "all";
  const ALL_TYPES = DOC_TYPES.map((t) => t.key);

  const [types, setTypes] = useState(() => {
    if (isSingleTypeMode) return [doc_type];
    return ALL_TYPES;
  });

  // ✅ 게시판(라우트) 이동 시 컴포넌트가 재마운트되지 않으면
  //    types가 이전 값으로 남아 목록이 갱신되지 않는 문제가 발생.
  //    doc_type(또는 pathname) 변경에 맞춰 state를 동기화하고 즉시 재조회한다.
  useEffect(() => {
    setPage(1);
    setQ("");
    setSearchField("title");
    setDateFilter("all");
    setStartDate("");
    setEndDate("");

    if (doc_type && doc_type !== "all") setTypes([doc_type]);
    else setTypes(ALL_TYPES);

    // searchTrigger로만 조회가 트리거되도록 되어 있으므로 이동 시 1회 올려준다.
    setSearchTrigger((v) => v + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc_type, location.pathname]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState("");

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const [searchField, setSearchField] = useState("title");
  const [q, setQ] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);

  const applySearch = () => {
    setPage(1);
    setSearchTrigger((v) => v + 1);
  };

  const docTypeCsv = types.join(",");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const params = {
          project: projectId,
          q,
          search_field: searchField,
          page,
          page_size: pageSize,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          doc_type: docTypeCsv,
        };

        const res = await listSafetyDocs(params);
        setRows(res.results || []);
        setTotal(res.total || 0);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (projectId) load();
  }, [projectId, page, pageSize, types, searchTrigger]);

  const toggleType = (t) => {
    setPage(1);
    setTypes((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
    );
  };

  // ---------------------------------------
  // Column 정의
  // ---------------------------------------
  const columns = [
    {
      key: "title",
      header: "제목",
      render: (r) => (
        <div className="truncate max-w-[480px]">{r.title || "-"}</div>
      ),
    },
    // {
    //   key: "doc_type",
    //   header: "문서 유형",
    //   render: (r) => (
    //     <span className="px-2 py-[2px] rounded-md text-xs border bg-gray-50 text-gray-700">
    //       {getDocTypeLabel(r.doc_type)}
    //     </span>
    //   ),
    // },
    {
      key: "status",
      header: "상태",
      render: (r) => {
        const p = r.progress; // ✅ 백엔드에서 내려준 meta.progress
        const label = p
          ? PROGRESS_LABEL[p] || "-"
          : STATUS_LABEL[r.status] || "-";
        const cls = p
          ? PROGRESS_COLOR[p] || "bg-gray-50 text-gray-500"
          : STATUS_COLOR[r.status] || "bg-gray-50 text-gray-500";
        return (
          <span
            className={`px-2 py-[1px] rounded-full text-xs font-medium ${cls}`}
          >
            {label}
          </span>
        );
      },
    },
    {
      key: "created_by",
      header: "작성자",
      render: (r) =>
        typeof r.created_by === "string"
          ? r.created_by || "-"
          : r.created_by?.name || "-",
    },
    {
      key: "created_at",
      header: "작성일",
      render: (r) =>
        r.created_at ? new Date(r.created_at).toLocaleDateString("ko-KR") : "-",
    },
    {
      key: "updated_at",
      header: "수정일",
      render: (r) =>
        r.updated_at ? new Date(r.updated_at).toLocaleDateString("ko-KR") : "-",
    },
  ];

  const goDetail = (row) => {
    navigate(`/project/${projectId}/safety/${row.doc_type}/${row.dns}/view`);
  };

  // ---------------------------------------
  // 라벨 (상단 페이지 헤더)
  // ---------------------------------------
  const displayBoardLabel = useMemo(() => {
    if (isSingleTypeMode) return `${getDocTypeLabel(doc_type)} 목록`;
    return "안전 관리";
  }, [doc_type, isSingleTypeMode]);

  // ---------------------------------------
  // UI 렌더링
  // ---------------------------------------
  return (
    <div className="p-6">
      {/* 상단 헤더 */}
      <PageHeader>{displayBoardLabel}</PageHeader>

      {/* Toolbar */}
      <BoardToolbar
        left={
          doc_type === "all" && (
            <>
              {DOC_TYPES.map((t) => {
                const active = types.includes(t.key);
                return (
                  <button
                    key={t.key}
                    onClick={() => toggleType(t.key)}
                    className={`rounded-full border px-3 py-1 text-sm ${
                      active
                        ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
              <button
                onClick={() => setTypes(ALL_TYPES)}
                className="rounded-full border px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
              >
                전체
              </button>
              <button
                onClick={() => setTypes([])}
                className="rounded-full border px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
              >
                없음
              </button>
            </>
          )
        }
        right={
          <>
            <SearchBar
              q={q}
              onChangeQ={setQ}
              onSearch={applySearch}
              searchField={searchField}
              onChangeSearchField={setSearchField}
              dateFilter={dateFilter}
              onChangeDateFilter={setDateFilter}
              startDate={startDate}
              endDate={endDate}
              onDateChange={({ start, end }) => {
                setStartDate(start);
                setEndDate(end);
              }}
              pageSize={pageSize}
              onChangePageSize={setPageSize}
            />

            <button
              className="btn-create ml-3"
              onClick={() => {
                if (doc_type && doc_type !== "all") {
                  navigate(`/project/${projectId}/safety/${doc_type}/new`);
                } else {
                  // 전체 게시판 등에서는 기존 "문서 만들기" 페이지로 이동
                  navigate(`/project/${projectId}/safety/create`);
                }
              }}
            >
              새 문서 등록
            </button>
          </>
        }
      />

      {/* 테이블 */}
      <BoardTable
        columns={columns}
        rows={rows}
        loading={loading}
        error={error}
        keyField="dns"
        onRowClick={goDetail}
      />

      {/* 페이지네이션 */}
      <BoardPagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
      />
    </div>
  );
}
